import { useEffect, useRef, useState } from 'react'
import { Smile, Hourglass } from 'lucide-react'
import { QUICK_REACTIONS, useVoiceStore } from '../../stores/voiceStore'
import { useLivekitStore } from '../../stores/livekitStore'

// Bottom-anchored popover for sending live emoji reactions during a
// voice/video call. Mirrors the SoundboardPicker layout so the controls
// row stays visually consistent. Sending is rate-limited inside
// voiceStore.sendReaction (returns false when blocked) — we surface the
// throttle by briefly de-emphasising the button so the user can tell
// their click registered without spamming the room.
export default function ReactionsPicker() {
  const [open, setOpen] = useState(false)
  const [throttled, setThrottled] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const sendReaction = useVoiceStore((s) => s.sendReaction)
  // OSPROD-61: BRB toggle. Read-and-write so the button can render its
  // active state and flip the flag in one click. Identity matches the
  // local participant — same key voiceStore.toggleLocalAway uses.
  const toggleLocalAway = useVoiceStore((s) => s.toggleLocalAway)
  const myIdentity = useLivekitStore((s) => s.room?.localParticipant?.identity)
  const isAway = useVoiceStore((s) => (myIdentity ? s.awayParticipants.has(myIdentity) : false))

  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      if (!ref.current) return
      if (!ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  const onPick = (emoji: string) => {
    const ok = sendReaction(emoji)
    if (!ok) {
      setThrottled(true)
      setTimeout(() => setThrottled(false), 250)
    }
    // Keep the popover open — users tend to fire two or three reactions
    // back-to-back ("👍🎉👏"). Closing on every click would force them
    // to re-open the picker each time.
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        title="Send a reaction"
        className={`w-10 h-10 rounded-full flex items-center justify-center transition duration-150 ${
          open
            ? 'bg-primary/20 text-primary'
            : throttled
              ? 'bg-muted/50 text-muted-foreground'
              : 'bg-muted text-foreground hover:bg-muted/80'
        }`}
      >
        <Smile size={20} />
      </button>
      {open && (
        // OSPROD-61: rounded-full pill became a rounded-2xl card so we
        // can stack the BRB action under the emoji row without the
        // shape looking awkward. Width is content-driven; the BRB row
        // sets the minimum so the emoji row sits left-aligned under it.
        <div
          className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-card rounded-2xl ring-1 ring-border shadow-lg z-30 p-1.5 flex flex-col gap-1"
          role="menu"
        >
          <div className="flex items-center gap-1">
            {QUICK_REACTIONS.map((emoji) => (
              <button
                key={emoji}
                onClick={() => onPick(emoji)}
                title={`Send ${emoji}`}
                className="w-9 h-9 rounded-full flex items-center justify-center text-xl hover:bg-accent active:scale-95 transition"
              >
                {emoji}
              </button>
            ))}
          </div>
          {/* OSPROD-61: Be right back. Toggle flag, broadcast over the
              data channel, hourglass overlay appears on this user's
              tile for everyone. Active style mirrors the raise-hand
              button so the visual language stays consistent across
              "step away from the mic" affordances. */}
          <button
            onClick={() => {
              toggleLocalAway()
              setOpen(false)
            }}
            title={isAway ? "Cancel — I'm back" : "Be right back"}
            className={`w-full px-3 py-1.5 rounded-lg flex items-center gap-2 text-sm transition ${
              isAway
                ? 'bg-yellow-500/20 text-yellow-500 hover:bg-yellow-500/30'
                : 'text-foreground hover:bg-accent'
            }`}
          >
            <Hourglass size={14} />
            <span>{isAway ? "I'm back" : 'Be right back'}</span>
          </button>
        </div>
      )}
    </div>
  )
}
